import { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { CheckCircle2, XCircle, Trash2, Music2, Mail, Phone, Instagram, Disc3, Pencil, Check, X, BarChart3, Play, Download } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("token");

export default function AdminCDSubmissions() {
  const [cds, setCds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null); // {cdId, idx}
  const [editValue, setEditValue] = useState("");
  const [statsModal, setStatsModal] = useState(null); // {cd}
  const [editingTrackPlays, setEditingTrackPlays] = useState(null); // {cdId, trackId}
  const [trackPlaysValue, setTrackPlaysValue] = useState("");

  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = async () => {
    setLoading(true);
    try {
      const url = `${API}/api/cd-submissions${filter ? `?status=${filter}` : ""}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setCds(arr);
      // Se aba default 'pending' está vazia na primeira carga, troca pra 'approved' automaticamente
      if (filter === "pending" && arr.length === 0 && !autoSwitched) {
        setAutoSwitched(true);
        setFilter("approved");
        return;
      }
    } catch {
      toast.error("Erro ao carregar");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/cd-submissions/${id}/status`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(status === "approved" ? "CD aprovado!" : status === "rejected" ? "CD rejeitado" : "Atualizado");
        load();
      } else toast.error("Erro");
    } catch { toast.error("Erro de conexão"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deletar este CD permanentemente?")) return;
    try {
      const res = await fetch(`${API}/api/cd-submissions/${id}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Deletado"); load(); }
    } catch { toast.error("Erro"); }
  };

  const startEditTrack = (cdId, idx, currentTitle) => {
    setEditingTrack({ cdId, idx });
    setEditValue(currentTitle || "");
  };

  const saveTrackTitle = async () => {
    if (!editingTrack) return;
    const { cdId, idx } = editingTrack;
    const title = editValue.trim();
    if (!title) { toast.error("Título não pode ficar vazio"); return; }
    try {
      const res = await fetch(`${API}/api/cd-submissions/${cdId}/track/${idx}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Erro");
      }
      toast.success("Faixa renomeada");
      setEditingTrack(null);
      setEditValue("");
      // Update locally without full reload
      setCds(prev => prev.map(cd => cd.id !== cdId ? cd : ({
        ...cd,
        tracks: (cd.tracks || []).map((t, i) => i === idx ? { ...t, title } : t),
      })));
    } catch (err) {
      toast.error("Erro: " + err.message);
    }
  };

  const removeTrack = async (cdId, idx, title) => {
    if (!window.confirm(`Remover a faixa "${title}" deste CD? Não pode ser desfeito.`)) return;
    try {
      const res = await fetch(`${API}/api/cd-submissions/${cdId}/track/${idx}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Erro");
      }
      toast.success("Faixa removida");
      // Reload to get re-numbered tracks
      load();
    } catch (err) {
      toast.error("Erro: " + err.message);
    }
  };

  const saveCdStats = async (cdId, plays, downloads) => {
    try {
      const res = await fetch(`${API}/api/admin/cd-submissions/${cdId}/stats`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          plays: plays === "" ? null : Number(plays),
          downloads: downloads === "" ? null : Number(downloads),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erro");
      toast.success("Stats do CD atualizadas");
      setStatsModal(null);
      // local update
      setCds(prev => prev.map(c => c.id !== cdId ? c : ({
        ...c,
        plays: plays === "" ? c.plays : Number(plays),
        downloads: downloads === "" ? c.downloads : Number(downloads),
      })));
    } catch (e) { toast.error("Erro: " + e.message); }
  };

  const startEditTrackPlays = (cdId, trackId, current) => {
    setEditingTrackPlays({ cdId, trackId });
    setTrackPlaysValue(String(current ?? 0));
  };

  const saveTrackPlays = async () => {
    if (!editingTrackPlays) return;
    const { cdId, trackId } = editingTrackPlays;
    const plays = Math.max(0, parseInt(trackPlaysValue || "0", 10) || 0);
    try {
      const res = await fetch(`${API}/api/admin/cd-submissions/${cdId}/tracks/${trackId}/stats`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ plays }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erro");
      toast.success("Plays da faixa atualizado");
      setEditingTrackPlays(null);
      setCds(prev => prev.map(c => c.id !== cdId ? c : ({
        ...c,
        tracks: (c.tracks || []).map(t => t.id === trackId ? { ...t, plays } : t),
      })));
    } catch (e) { toast.error("Erro: " + e.message); }
  };

  const filters = [
    { value: "pending", label: "Pendentes" },
    { value: "approved", label: "Aprovados" },
    { value: "rejected", label: "Rejeitados" },
    { value: "", label: "Todos" },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">CDs Enviados</h1>
            <p className="text-zinc-500 mt-1 text-sm">Modere os CDs enviados pela vitrine pública</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {filters.map(f => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  filter === f.value ? "bg-[#FF007F] text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"
                }`}
                data-testid={`filter-${f.value || "all"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : cds.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-16 text-center">
            <Disc3 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Nenhum CD nesta categoria</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {cds.map(cd => (
              <div key={cd.id} className="bg-[#121212] border border-white/10 rounded-2xl overflow-hidden" data-testid={`admin-cd-${cd.id}`}>
                <div className="flex items-start gap-4 p-4">
                  <img src={`${API}${cd.cover_url}`} alt="" className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        cd.status === "approved" ? "bg-green-500/20 text-green-400" :
                        cd.status === "rejected" ? "bg-red-500/20 text-red-400" :
                        "bg-amber-500/20 text-amber-400"
                      }`}>
                        {cd.status === "approved" ? "APROVADO" : cd.status === "rejected" ? "REJEITADO" : "PENDENTE"}
                      </span>
                      <span className="text-xs text-zinc-500 flex items-center gap-1"><Music2 size={12}/> {cd.tracks?.length || 0} faixas</span>
                    </div>
                    <h3 className="font-bold text-white text-lg truncate">{cd.cd_title}</h3>
                    <p className="text-[#FF007F] font-semibold truncate">{cd.artist_name}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Mail size={12}/> {cd.email}</span>
                      {cd.whatsapp && <span className="flex items-center gap-1"><Phone size={12}/> {cd.whatsapp}</span>}
                      {cd.instagram && <span className="flex items-center gap-1"><Instagram size={12}/> {cd.instagram}</span>}
                      <span className="flex items-center gap-1 text-green-400" title="Plays do CD"><Play size={11}/> {(cd.plays ?? 0).toLocaleString("pt-BR")}</span>
                      <span className="flex items-center gap-1 text-blue-400" title="Downloads do CD"><Download size={11}/> {(cd.downloads ?? 0).toLocaleString("pt-BR")}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {cd.status !== "approved" && (
                      <button onClick={() => updateStatus(cd.id, "approved")}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-lg flex items-center gap-1"
                        data-testid={`approve-${cd.id}`}>
                        <CheckCircle2 size={14}/> Aprovar
                      </button>
                    )}
                    {cd.status !== "rejected" && (
                      <button onClick={() => updateStatus(cd.id, "rejected")}
                        className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-bold rounded-lg flex items-center gap-1"
                        data-testid={`reject-${cd.id}`}>
                        <XCircle size={14}/> Rejeitar
                      </button>
                    )}
                    <button onClick={() => setStatsModal({ cd })}
                      className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white text-xs font-bold rounded-lg flex items-center gap-1 transition-colors"
                      data-testid={`stats-${cd.id}`}>
                      <BarChart3 size={14}/> Stats
                    </button>
                    <button onClick={() => remove(cd.id)}
                      className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-xs font-bold rounded-lg flex items-center gap-1 transition-colors"
                      data-testid={`delete-${cd.id}`}>
                      <Trash2 size={14}/> Excluir
                    </button>
                  </div>
                </div>

                <button onClick={() => setExpanded(expanded === cd.id ? null : cd.id)}
                  className="w-full bg-black/30 hover:bg-black/40 text-zinc-400 text-sm py-2 transition-colors">
                  {expanded === cd.id ? "Ocultar faixas" : `Ver ${cd.tracks?.length || 0} faixas`}
                </button>

                {expanded === cd.id && (
                  <div className="border-t border-white/5 divide-y divide-white/5">
                    {(cd.tracks || []).map((t, idx) => {
                      const isEditing = editingTrack?.cdId === cd.id && editingTrack?.idx === idx;
                      return (
                        <div key={t.audio_url} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                          <span className="text-zinc-600 tabular-nums w-6 flex-shrink-0">{String(t.track_number || idx + 1).padStart(2, "0")}</span>
                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") saveTrackTitle(); if (e.key === "Escape") setEditingTrack(null); }}
                                autoFocus
                                className="flex-1 bg-black/40 border border-[#FF007F]/40 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#FF007F]"
                                data-testid={`edit-track-input-${cd.id}-${idx}`}
                              />
                              <button onClick={saveTrackTitle} className="p-1.5 text-green-400 hover:bg-green-500/15 rounded" title="Salvar" data-testid={`save-track-${cd.id}-${idx}`}>
                                <Check size={14}/>
                              </button>
                              <button onClick={() => { setEditingTrack(null); setEditValue(""); }} className="p-1.5 text-zinc-400 hover:bg-white/10 rounded" title="Cancelar">
                                <X size={14}/>
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 truncate" data-testid={`track-title-${cd.id}-${idx}`}>{t.title}</span>
                              {editingTrackPlays?.cdId === cd.id && editingTrackPlays?.trackId === t.id ? (
                                <span className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    value={trackPlaysValue}
                                    onChange={(e) => setTrackPlaysValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveTrackPlays(); if (e.key === "Escape") setEditingTrackPlays(null); }}
                                    autoFocus
                                    placeholder="plays"
                                    className="w-24 bg-black/40 border border-green-500/40 rounded px-2 py-0.5 text-green-300 text-xs font-bold tabular-nums focus:outline-none focus:border-green-500"
                                    data-testid={`edit-track-plays-input-${cd.id}-${idx}`}
                                  />
                                  <button onClick={saveTrackPlays} className="p-1 text-green-400 hover:bg-green-500/15 rounded" title="Salvar plays">
                                    <Check size={12}/>
                                  </button>
                                  <button onClick={() => setEditingTrackPlays(null)} className="p-1 text-zinc-400 hover:bg-white/10 rounded" title="Cancelar">
                                    <X size={12}/>
                                  </button>
                                </span>
                              ) : (
                                <button
                                  onClick={() => startEditTrackPlays(cd.id, t.id, t.plays || 0)}
                                  className="flex items-center gap-1 text-[11px] text-green-400 hover:text-green-300 hover:bg-green-500/10 px-1.5 py-0.5 rounded tabular-nums"
                                  title="Editar plays desta faixa"
                                  data-testid={`track-plays-${cd.id}-${idx}`}
                                >
                                  <Play size={10}/> {(t.plays ?? 0).toLocaleString("pt-BR")}
                                </button>
                              )}
                              <audio controls src={`${API}${t.audio_url}`} className="h-7 max-w-[180px]" preload="none"/>
                              <button onClick={() => startEditTrack(cd.id, idx, t.title)}
                                className="p-1.5 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors" title="Editar nome"
                                data-testid={`edit-track-${cd.id}-${idx}`}>
                                <Pencil size={14}/>
                              </button>
                              <button onClick={() => removeTrack(cd.id, idx, t.title)}
                                className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Remover faixa"
                                data-testid={`remove-track-${cd.id}-${idx}`}>
                                <Trash2 size={14}/>
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {statsModal && (
        <CDStatsModal
          cd={statsModal.cd}
          onClose={() => setStatsModal(null)}
          onSave={saveCdStats}
        />
      )}
    </AdminLayout>
  );
}

function CDStatsModal({ cd, onClose, onSave }) {
  const [plays, setPlays] = useState(cd?.plays ?? 0);
  const [downloads, setDownloads] = useState(cd?.downloads ?? 0);

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#101010] border border-white/10 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <BarChart3 size={18} className="text-[#FF007F]" /> Editar Stats do CD
          </h3>
          <button onClick={onClose} className="p-1 text-zinc-400 hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-zinc-500 text-xs mb-4 truncate">{cd.artist_name} — {cd.cd_title}</p>

        <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1">Plays totais</label>
        <div className="flex items-center gap-2 mb-4">
          <Play size={16} className="text-green-400" />
          <input
            type="number"
            min="0"
            value={plays}
            onChange={(e) => setPlays(e.target.value)}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white tabular-nums focus:outline-none focus:border-[#FF007F]"
            data-testid="cd-stats-plays-input"
          />
        </div>

        <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1">Downloads totais</label>
        <div className="flex items-center gap-2 mb-6">
          <Download size={16} className="text-blue-400" />
          <input
            type="number"
            min="0"
            value={downloads}
            onChange={(e) => setDownloads(e.target.value)}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white tabular-nums focus:outline-none focus:border-[#FF007F]"
            data-testid="cd-stats-downloads-input"
          />
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm font-bold">
            Cancelar
          </button>
          <button
            onClick={() => onSave(cd.id, plays, downloads)}
            className="flex-1 px-4 py-2 bg-[#FF007F] hover:bg-[#FF3399] text-white rounded-lg text-sm font-bold"
            data-testid="cd-stats-save-btn"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
